'use client'
import { memo, type RefObject, useMemo, type FC } from 'react'
import styled from 'styled-components'
import { useDeviceType, useMount, useTranslator } from '@/hooks'
import type { TypeCmsProductDetail } from '@/schema'
import { SliderFlickity } from './commons'
import { CardProduct } from './CardProduct'
import { designGrid, getP18, mq, rgbAlpha, vw } from '@/styles'

type Props = {
  data: TypeCmsProductDetail['related_products']
  ref: RefObject<HTMLElement>
}

const getSlides = (data:TypeCmsProductDetail['related_products']) => data.map((item, idx) => {
  return (
    <DivSlide key={idx} className='slide'>
      <CardProduct data={item} lazyLoad={idx > 4} />
    </DivSlide>
  )
})

const headingId = 'relateds'
export const SectionRelateds:FC<Props> = memo(({ data, ref}) => {
  const { t } = useTranslator()
  const { isMobile } = useDeviceType()
  const Slides = useMemo(() => getSlides(data), [data])
  const isMount = useMount()

  return (
    <Section ref={ref} aria-labelledby={headingId}>
      <h2 id={headingId}>{t('project.product.relateds_products')}</h2>
      {isMount && <SliderFlickity options={{ cellAlign: 'left', freeScroll:false, wrapAround: false, draggable: data.length > 4 ? true : false, groupCells: isMobile ? 2 : 4, prevNextButtons: data.length > 4}}>
        {Slides}
      </SliderFlickity>}
    </Section>
  )
})

const Section = styled.section`
  ${designGrid({})}
  overflow-x: hidden;
  padding: 90px 0 100px;
  position: relative;

  ${mq.greaterThan('tablet')}{
    margin: 0;
    padding: 110px 0 100px;
  }

  h2 {
    ${getP18()}
    grid-area: 1 / 1 / span 1 / span 6;
    margin-bottom: ${vw(30, 'mobile')};
    text-align: left;

    ${mq.greaterThan('tablet')}{
      grid-area: 1 / 1 / span 1 / span 3;
      margin-bottom: ${vw(40, 'desktop')};
    }
  }

  > div {
    display: grid;
    grid-area: 2 / 1 / span 1 / span 6;
    grid-row-gap: ${vw(20, 'mobile')};
    grid-template-columns: 1fr 1fr;
    overflow-x: hidden;

    ${mq.greaterThan('tablet')}{
      grid-area: 2 / 1 / span 1 / span 12;
      grid-template-columns: repeat(4, 1fr);
      grid-row-gap: 0;
    }
  }

  .flickity-button {
    align-items: center;
    background: ${rgbAlpha('--color-creme', 1)};
    border-radius: 50%;
    bottom: 45%;
    display: flex;
    height: 30px;
    justify-content: center;
    position: absolute;
    user-select: none;
    width: 30px;
    z-index: 100;

    svg {
      height:15px;
      width:15px;
    }

    &.previous {
      left: 5%;
    }

    &.next {
      left: calc(100vw - 90px);
    }

    &:disabled {
      display: none;
      opacity: .25;
      pointer-events: none;
    }

    .flickity-button-icon {
      fill: var(--color-white);
    }
  }


  `

const DivSlide = styled.div`
  margin-right: ${vw(23, 'mobile')};
  width: auto;

  ${mq.greaterThan('tablet')} {
    margin-right: ${vw(23, 'desktop')};
  }

  &:last-child {
    margin-right: 0;
  }

  div.media {
    min-width: ${vw(271, 'mobile')};

    ${mq.greaterThan('tablet')} {
      min-width: ${vw(320, 'desktop')};
    }
  }
  
`
